import AddIcon from "@mui/icons-material/Add";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import MinusIcon from "@mui/icons-material/Remove";
import SecurityIcon from "@mui/icons-material/Security";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import CondensedCell from "_shared/_views/tables/condensed-cell";
import { NoValueDash } from "_shared/_views/tables/no-value-dash";
import RowActions from "_shared/_views/tables/row-actions";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import {
  CollectionToolbar,
  CollectionToolbarSearch,
  CollectionToolbarViewButtons,
  searchTextFieldProps,
} from "_shared/_views/tables/table-toolbar";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import Guid from "_shared/guids/guid";
import {
  createMRTColumnHelper,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_TableInstance,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState,
} from "material-react-table";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import React, { type ReactNode, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import { principalChanged } from "users/__session/principal-change";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import AuthEmailAPIService, { AuthResponse } from "users/_api/auth-email-api-service";
import UserInformation from "users/information/user-information";
import OnboardingWorkResolution from "users/super-users/_views/user-administration/onboarding-work-resolution";
import SearchUsersAPIRequest, { type ColumnSort } from "users/super-users/_api/commands/search-users-api-request";
import {
  useLoginAsUserMutation,
  useRegisterSuperUserMutation,
  useSearchUsersQuery,
  useUnregisterSuperUserMutation,
} from "users/super-users/_api/super-user-api";

type UsersAdminProps = {
  onProfileView: (type: string, id?: string) => void;
};

const columnHelper = createMRTColumnHelper<UserInformation>();

/** The Name column's id: one column for the user's whole name, with their company under it (#6722). */
export const NAME_COLUMN = "name";
/** The Company column's id, which is also the search's sort and filter parameter for it. */
export const COMPANY_COLUMN = "companyName";

/**
 * The columns the Name column stands in for, put away until the reader shows them. Each is still in
 * Show/hide, where it sorts and filters on its own: the search filters first and last name separately,
 * and has no one parameter a Name filter could send.
 */
export const HIDDEN_UNTIL_CHOSEN: Readonly<Record<string, boolean>> = {
  firstName: false,
  lastName: false,
  [COMPANY_COLUMN]: false,
};

export function getUserDisplayName(user: UserInformation): string {
  return user.name.fullName.trim() || (user.email?.value ?? user.id.value);
}

/**
 * The order the search is asked for. The search orders by one column, and has no full-name column:
 * the Name column orders by last name, which is also the search's own order when none is asked for.
 */
export function searchSortFrom(sorting: SortingState): ColumnSort | undefined {
  const sort = sorting[0];
  if (sort?.id !== NAME_COLUMN) return sort;
  return { id: "lastName", desc: sort.desc };
}

/** A loading placeholder row reaches the accessors as `{ column: null }`, not as a user. */
function loaded(user: UserInformation): UserInformation | undefined {
  return user instanceof UserInformation ? user : undefined;
}

function UserNameCell(
  props: Readonly<{
    user: UserInformation;
    table: MRT_TableInstance<UserInformation>;
    renderCompany: (user: UserInformation, companyName: ReactNode) => ReactNode;
  }>,
) {
  // SearchMatch reads the term from MRT's one mutable table instance; a memoized cell keeps the old one.
  "use no memo";

  const { user, table, renderCompany } = props;
  // Shown, the Company column has the company to itself; hidden, as it is until chosen, it reads here.
  const isCompanyUnderName = table.getState().columnVisibility?.[COMPANY_COLUMN] === false;
  return (
    <CondensedCell
      data-testid="user-name-cell"
      title={<SearchMatch text={getUserDisplayName(user)} table={table} />}
      secondary={isCompanyUnderName && renderCompany(user, <SearchMatch text={user.companyName} table={table} />)}
    />
  );
}

export default function UsersAdmin(props: Readonly<UsersAdminProps>) {
  const { onProfileView } = props;
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const confirm = useConfirmDialog();
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const navigate = useNavigate();
  const searchRequest = useMemo(
    () =>
      new SearchUsersAPIRequest(
        query ?? "",
        pagination.pageIndex,
        pagination.pageSize,
        searchSortFrom(sorting),
        columnFilters,
      ),
    [query, pagination.pageIndex, pagination.pageSize, sorting, columnFilters],
  );
  const usersQuery = useSearchUsersQuery({ request: searchRequest });
  const [registerSuperUserMutation] = useRegisterSuperUserMutation();
  const [unregisterSuperUser] = useUnregisterSuperUserMutation();
  const [loginAsSelectedUser] = useLoginAsUserMutation();
  // `currentData`, not `data`: while a new search is on its way, `data` still holds the previous
  // search's rows and total, which the table would show as the answer to the new one (#6589).
  const data = usersQuery.currentData?.data ?? [];
  const rowCount = usersQuery.currentData?.totalElements ?? 0;
  const isSearching = usersQuery.isFetching && !usersQuery.currentData;

  // Paging is manual, so a new filter would otherwise keep the page index and could land past
  // the last page of the narrower result.
  function returnToFirstPage() {
    setPagination((current) => (current.pageIndex === 0 ? current : { ...current, pageIndex: 0 }));
  }

  const refreshUsers = useCallback(async () => {
    await usersQuery.refetch();
  }, [usersQuery]);

  async function registerSuperUser(user: UserInformation) {
    if (!user) return;
    const response = await confirm({
      title: `Register ${getUserDisplayName(user)} as a superuser?`,
      message: "Are you sure you want to register this user as a superuser?",
      okButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await registerSuperUserMutation({ userId: user.id }).unwrap();
      await refreshUsers();
    } catch {
      enqueueSnackbar("Couldn't register superuser", { variant: "error" });
    }
  }

  async function unRegisterSuperUser(user: UserInformation) {
    const response = await confirm({
      title: `Unregister ${getUserDisplayName(user)} as a superuser?`,
      message: "Are you sure you want to unregister this user as a superuser?",
      okButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await unregisterSuperUser({ userId: user.id }).unwrap();
      await refreshUsers();
    } catch {
      enqueueSnackbar("Couldn't unregister superuser", { variant: "error" });
    }
  }

  async function resetUserPassword(data: UserInformation | UserInformation[]) {
    let user: UserInformation;
    if (Array.isArray(data)) {
      user = data[0];
    } else {
      user = data;
    }
    const response = await confirm({
      title: `Reset ${getUserDisplayName(user)}'s password?`,
      message: "Are you sure you want to reset this user's password?",
      okButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await AuthEmailAPIService.requestPasswordReset(user.email!);
      enqueueSnackbar("Sent password reset email to user", { variant: "success" });
    } catch {
      enqueueSnackbar("Couldn't reset user password", { variant: "error" });
    }
  }

  async function loginAsUser(data: UserInformation | UserInformation[]) {
    let user: UserInformation;
    if (Array.isArray(data)) {
      user = data[0];
    } else {
      user = data;
    }
    const userId = user.id;
    const response = await confirm({
      title: `Login as ${getUserDisplayName(user)}?`,
      message: "Are you sure you want to login as this user?",
      okButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      const authResponse: AuthResponse = await loginAsSelectedUser({ userId }).unwrap();
      session.initializeFromAuthResponse(authResponse);
      dispatch(principalChanged());
      navigate("/dashboard", { replace: true });
    } catch {
      enqueueSnackbar("Couldn't login as user", { variant: "error" });
    }
  }

  const renderCompanyColumn = useCallback(
    (user: UserInformation, companyName: ReactNode) => {
      if (!user.companyId) return null;
      return (
        <Link
          component="button"
          type="button"
          color="inherit"
          underline="always"
          sx={{ font: "inherit", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", verticalAlign: "bottom" }}
          onClick={() => {
            onProfileView("company", user?.companyId?.value);
          }}
        >
          {companyName}
        </Link>
      );
    },
    [onProfileView],
  );

  const renderEmailCell = useCallback((email: ReactNode) => {
    return (
      <Typography
        variant="body2"
        sx={{
          fontWeight: "500",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {email}
      </Typography>
    );
  }, []);

  const columns = useMemo(
    () => [
      // The search matches email, first name, last name and company (#6590). MRT hands each custom
      // cell its value with the match marked, and the cell draws that rather than the bare value.
      columnHelper.accessor((user) => (loaded(user) ? getUserDisplayName(user) : ""), {
        header: "Name",
        id: NAME_COLUMN,
        // Sorted by last name (`searchSortFrom`), and filtered through First name and Last name.
        enableColumnFilter: false,
        meta: { kind: "name" },
        Cell: ({ row, table }) => (
          <UserNameCell user={row.original} table={table} renderCompany={renderCompanyColumn} />
        ),
      }),
      columnHelper.accessor((user) => loaded(user)?.email?.value ?? "", {
        header: "Email",
        id: "email",
        meta: { kind: "text", fold: { into: NAME_COLUMN, priority: 1, render: (user) => user.email?.value } },
        Cell: ({ renderedCellValue }) => renderEmailCell(renderedCellValue),
      }),
      columnHelper.accessor((user) => loaded(user)?.name.firstName ?? "", {
        header: "First name",
        id: "firstName",
        meta: { kind: "text" },
      }),
      columnHelper.accessor((user) => loaded(user)?.name.lastName ?? "", {
        header: "Last name",
        id: "lastName",
        meta: { kind: "text" },
      }),
      columnHelper.accessor((user) => loaded(user)?.companyName ?? "", {
        header: "Company",
        id: COMPANY_COLUMN,
        meta: { kind: "text" },
        Cell: ({ renderedCellValue, row }) => renderCompanyColumn(row.original, renderedCellValue),
      }),
      columnHelper.accessor((user) => loaded(user)?.onboardingWork?.state ?? "", {
        header: "Onboarding",
        id: "onboardingState",
        // Onboarding state is not a column of the search query, so the server can neither sort
        // nor filter on it. Offering either control would answer with a 400 or do nothing.
        enableSorting: false,
        enableColumnFilter: false,
        // Nor does the search read it, so no match is marked here (#6590).
        enableGlobalFilter: false,
        meta: { kind: "status" },
        Cell: ({ row }) => row.original.onboardingWork
          ? <OnboardingWorkResolution onboardingWork={row.original.onboardingWork} onChanged={() => void refreshUsers()} />
          : <NoValueDash />,
      }),
    ],
    [refreshUsers, renderCompanyColumn, renderEmailCell],
  );

  const tableOptions: MRT_TableOptions<UserInformation> = {
    columns,
    data,
    enableRowSelection: true,
    enableTableHead: true,
    enableRowActions: true,
    enableColumnPinning: true,
    enableGlobalFilter: true,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
      columnVisibility: { ...HIDDEN_UNTIL_CHOSEN },
      showGlobalFilter: true,
    },
    // The search and the view buttons on one wrapping row, the toolbar every collection table draws
    // (#6041). MRT's own put the buttons on a line of their own and the search on the next, right-
    // aligned under them, which on a phone cost two rows before the first user.
    positionGlobalFilter: "none",
    renderTopToolbarCustomActions: ({ table }) => (
      <CollectionToolbar>
        <CollectionToolbarSearch>
          <GlobalFilterTextField table={table} />
        </CollectionToolbarSearch>
        <CollectionToolbarViewButtons>
          <TableToolsMenu table={table} />
        </CollectionToolbarViewButtons>
      </CollectionToolbar>
    ),
    // An empty fragment rather than `null`: MRT reads this slot with `??`, and a nullish return would
    // draw its default buttons a second time.
    renderToolbarInternalActions: () => <></>,
    muiSearchTextFieldProps: ({ table }) => ({
      ...searchTextFieldProps({ searches: "users", table }),
      size: "small",
      variant: "outlined",
    }),
    muiExpandButtonProps: ({ row }) => ({
      children: row.getIsExpanded() ? <MinusIcon /> : <AddIcon />,
    }),
    renderRowActions: ({ row }) => {
      const user = row.original;
      const userName = getUserDisplayName(user);
      return (
        <RowActions
          ariaLabel={`Actions for ${userName}`}
          actions={[
            user.isSuperUser
              ? {
                  key: "superuser",
                  label: "Unregister As Superuser",
                  ariaLabel: `Unregister ${userName} as superuser`,
                  icon: <SecurityIcon />,
                  destructive: true,
                  onClick: () => {
                    void unRegisterSuperUser(user);
                  },
                }
              : {
                  key: "superuser",
                  label: "Register As Superuser",
                  ariaLabel: `Register ${userName} as superuser`,
                  icon: <SecurityIcon />,
                  onClick: () => {
                    void registerSuperUser(user);
                  },
                },
            {
              key: "reset-password",
              label: "Force Password Reset",
              ariaLabel: `Force password reset for ${userName}`,
              icon: <VpnKeyIcon />,
              onClick: () => {
                void resetUserPassword(user);
              },
            },
            {
              key: "login-as",
              label: "Login As User",
              ariaLabel: `Login as ${userName}`,
              icon: <LockOpenIcon />,
              onClick: () => {
                void loginAsUser(user);
              },
            },
          ]}
        />
      );
    },
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    manualPagination: true,
    manualSorting: true,
    rowCount: rowCount,
    onColumnFiltersChange: (updater) => {
      setColumnFilters(updater);
      returnToFirstPage();
    },
    onGlobalFilterChange: (value: string | undefined) => {
      setQuery(value);
      returnToFirstPage();
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    muiTableContainerProps: { sx: { flex: 1 } },
    muiTablePaperProps: { sx: { height: "100%", display: "flex", flexDirection: "column" } },
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: usersQuery.isError,
      isLoading: usersQuery.isFetching,
      onRetry: () => void usersQuery.refetch(),
      subject: "users",
    }),
    muiToolbarAlertBannerProps: usersQuery.isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    state: {
      columnFilters,
      globalFilter: query,
      isLoading: isSearching,
      pagination,
      showAlertBanner: usersQuery.isError,
      showProgressBars: usersQuery.isFetching,
      sorting,
    },
  };

  return <MRTNoMemo<UserInformation> options={tableOptions} persistColumnVisibilityAs="users" cards />;
}
